// ═══════════════════════════════════════════════════════════════════════════
// snapshotMetricas — el registro central de métricas EN PANTALLA (T7-g, §6.2)
// (diseño docs/DISENO_T7g_snapshot-fab.md §2.1)
// ═══════════════════════════════════════════════════════════════════════════
// Cada card/pantalla PUBLICA lo que está mostrando ({metrica, valor}, con la
// ventana resuelta a fechas ABSOLUTAS y los filtros de su query); el FAB de
// debugging lo serializa genérico en `contexto.snapshot` al crear un reporte.
// Un "el facturado está mal" viaja CON el número que el usuario veía y con
// cómo reproducir la query — la reconciliación deja de ser arqueología.
//
// Módulo PURO a nivel de módulo (sin React Context a propósito: no toca el
// árbol de providers y lo puede leer capturarContexto, que no es un hook).
// Los IDs de `metrica` usan los del diccionario canónico cuando existen
// (docs/diccionario-metricas.md) — paridad blindada por test con el parser
// real (snapshotMetricas.paridad.test.js).

import { useEffect } from 'react'

// Tope de métricas serializadas: el snapshot viaja DENTRO del doc del reporte
// (cap 1MB, §6.2). 50 escalares sobran para toda pantalla real de hoy; series
// e histogramas van a subcolección cuando exista la primera card que los
// necesite (desvío declarado del diseño §2.5).
export const TOPE_METRICAS_SNAPSHOT = 50

const registro = new Map() // origenId → { metricas, ventana, filtros }

/** Serialización SEGURA: un valor raro se VE (string acotado), jamás rompe el FAB. */
function valorSeguro(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'boolean') return v
  if (v === null || v === undefined) return null
  return String(v).slice(0, 200)
}

/**
 * Publica las métricas visibles de un origen (pantalla/card). Pisa lo
 * anterior del MISMO origen (la pantalla re-renderiza, el registro sigue).
 * Devuelve `despublicar()` — una pantalla desmontada no publica fantasmas.
 */
export function publicarMetricas(origenId, { metricas = [], ventana = null, filtros = null } = {}) {
  registro.set(String(origenId), { metricas, ventana, filtros })
  return () => { registro.delete(String(origenId)) }
}

/**
 * El snapshot serializable del momento. SIEMPRE devuelve shape completo:
 * `origenes: []` vacío significa "había registro y nadie publicaba" — que es
 * distinto de un reporte PRE-deploy sin el campo (§6.2: esos son "no
 * reproducible" por defecto). Vacío ≠ ausente, a propósito.
 */
export function snapshotActual() {
  const origenes = []
  let quedan = TOPE_METRICAS_SNAPSHOT
  for (const [origenId, d] of registro) {
    const metricas = (d.metricas || []).slice(0, Math.max(quedan, 0)).map((m) => ({
      metrica: String(m.metrica || '').slice(0, 80),
      valor: valorSeguro(m.valor),
      ...(m.unidad ? { unidad: String(m.unidad).slice(0, 20) } : {}),
    }))
    quedan -= metricas.length
    origenes.push({
      origen: origenId,
      metricas,
      ventana: d.ventana ? { desdeIso: String(d.ventana.desdeIso || ''), hastaIso: String(d.ventana.hastaIso || '') } : null,
      filtros: d.filtros
        ? Object.fromEntries(Object.entries(d.filtros).slice(0, 10).map(([k, v]) => [String(k).slice(0, 40), valorSeguro(v)]))
        : null,
    })
    if (quedan <= 0) break
  }
  return { capturadoEn: new Date().toISOString(), origenes }
}

/** Solo para tests: el registro no persiste entre casos. */
export function _limpiarRegistro() { registro.clear() }

/**
 * Hook de publicación: publica en cada cambio y despublica al desmontar.
 * `datos` debe venir memoizado del caller (useMemo) — este hook no compara.
 */
export function usePublicarMetricas(origenId, datos) {
  useEffect(() => {
    if (!datos) return undefined
    return publicarMetricas(origenId, datos)
  }, [origenId, datos])
}
